import { getRewardRouteToInfoPage } from '@/common/reward/route.registry';
import { InjectionFeature } from '@/common/reward/types';
import { useCrudActions } from '@/common/view/useCrudActions';
import {
    StarterPackFeatureMapping,
    StarterPackRewardItem,
    StarterPackRewardType
} from '@/modules/starter/pack/reward/types';
import { starterPackService } from '@/services/starter';
import { computed, Ref, ref } from 'vue';

export function useStarterPackReward(selectedRewardType?: Ref<number | StarterPackRewardType>) {
    const linkableRewards = ref<StarterPackRewardItem[]>([]);

    const fetchLinkableRewards = useCrudActions().executeAction({
        request: starterPackService.reward.getLinkableRewards,
        onSuccess: ({ data }) => {
            linkableRewards.value = data.map(
                (reward: any) =>
                    new StarterPackRewardItem(
                        reward.type,
                        StarterPackFeatureMapping.get(reward.type),
                        reward.itemId,
                        reward.itemName
                    )
            );
        },
        onError: () => void (linkableRewards.value = [])
    });

    const rewardOptions = computed(() => {
        if (!selectedRewardType) return [];

        return linkableRewards.value
            .filter(item => item.type === selectedRewardType.value)
            .map(item => ({ value: item.itemId, label: item.itemName }));
    });

    return { linkableRewards, fetchLinkableRewards, rewardOptions };
}

export function decorateReward<T extends { data: number; type: number }>(reward: T, items: StarterPackRewardItem[]) {
    const linkableItem = getRewardLinkableItem(items, reward.data, reward.type);

    return {
        ...reward,
        itemName: linkableItem.itemName,
        itemLink: getRewardRouteToInfoPage(linkableItem.feature, linkableItem.itemId)
    };
}

function getRewardLinkableItem(items: StarterPackRewardItem[], data: number, type: number) {
    if (type == StarterPackRewardType.NoAds) {
        return new StarterPackRewardItem(type, 0, 0, 'NoAds');
    }

    const feature = StarterPackFeatureMapping.get(type);
    if (feature === InjectionFeature.CreditTicket) {
        return new StarterPackRewardItem(type, feature, data, `${data} chip(s)`);
    }

    return items.find(item => item.itemId === data && item.feature === feature) ?? new StarterPackRewardItem();
}
